import { axiosInstance } from "../Axios/AxiosInstance";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

export const getAuthHeaders = () => {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("token"));
  } catch {
    token = localStorage.getItem("token");
  }
  return {
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
};

export const isAuthenticated = () => {
  try {
    return Boolean(JSON.parse(localStorage.getItem("token")));
  } catch {
    return Boolean(localStorage.getItem("token"));
  }
};

export const isPwaSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  window.isSecureContext;

export const isPushSupported = () =>
  isPwaSupported() && "PushManager" in window && "Notification" in window;

export const isInstalledAppShell = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const source = params.get("source") || "";
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true ||
    source === "twa" ||
    source === "pwa" ||
    source.startsWith("twa-") ||
    source.startsWith("pwa-")
  );
};

export const getBrowserNotificationPermission = () => {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
};

export const registerServiceWorker = async () => {
  if (!isPwaSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/",
    });
    await navigator.serviceWorker.ready;
    return registration;
  } catch (error) {
    console.error("[PWA] Service worker registration failed:", error);
    return null;
  }
};

export const subscribeToPushNotifications = async () => {
  if (!isPushSupported() || !isAuthenticated()) {
    return { success: false, reason: "unsupported_or_unauthenticated" };
  }

  try {
    const permission =
      Notification.permission === "granted"
        ? "granted"
        : await Notification.requestPermission();

    if (permission !== "granted") {
      return { success: false, reason: "permission_denied", permission };
    }

    const registration = await registerServiceWorker();
    if (!registration) {
      return { success: false, reason: "sw_failed" };
    }

    const vapidRes = await axiosInstance.get("notifications/vapid-public-key");
    const publicKey = vapidRes?.data?.publicKey;
    if (!publicKey) {
      return { success: false, reason: "no_vapid_key" };
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    const payload = subscription.toJSON();
    const ua = window.navigator.userAgent || "";
    await axiosInstance.post(
      "notifications/subscribe",
      {
        endpoint: payload.endpoint,
        keys: payload.keys,
        platform: /Android/i.test(ua) ? "android" : /iPhone|iPad/i.test(ua) ? "ios" : "desktop",
        source: isInstalledAppShell() ? "twa" : "web",
      },
      getAuthHeaders()
    );

    localStorage.setItem("push_subscribed", "1");
    return { success: true, permission: "granted" };
  } catch (error) {
    console.error("[PWA] Push subscribe failed:", error);
    return { success: false, reason: "subscribe_failed", error };
  }
};

export const initPwaForUser = async () => {
  await registerServiceWorker();
  if (
    isAuthenticated() &&
    typeof Notification !== "undefined" &&
    Notification.permission === "granted"
  ) {
    await subscribeToPushNotifications();
  }
};
